/*
* === ReviewApplicationPage.js (Enterprise UI/UX - Master Version) ===
* Features: No window.alerts, Toast Notifications, Confirm Modals, Dark Mode Aware, Smart Banking, JB Marks Custom Data, Full PDF Export, Profile Academic Results & Global Profile Sync
*/

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Card, CardContent, Box, CircularProgress, Alert, Grid, Divider,
  Select, MenuItem, FormControl, InputLabel, TextField, Button, Paper, List, ListItem,
  ListItemIcon, ListItemText, Slider, CardHeader, Link, Chip, IconButton,
  Avatar, AppBar, Toolbar, Dialog, DialogTitle, DialogContent, DialogActions,
  DialogContentText, Snackbar, Tooltip
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

// Icons
import DescriptionIcon from '@mui/icons-material/Description';
import PaymentsIcon from '@mui/icons-material/Payments';
import SchoolIcon from '@mui/icons-material/School';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ForumIcon from '@mui/icons-material/Forum';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; 
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArticleIcon from '@mui/icons-material/Article';
import RestoreIcon from '@mui/icons-material/Restore'; 
import GavelIcon from '@mui/icons-material/Gavel';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'; 
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import RuleIcon from '@mui/icons-material/Rule';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'; 
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'; 

const API_BASE_URL = 'https://brightsparc-api-520983012715.africa-south1.run.app';

const DetailItem = ({ title, data }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>{title}</Typography>
    <Typography variant="body1">{data && data !== 'null' ? data : 'N/A'}</Typography>
  </Box>
);

const parseData = (data) => {
    if (!data) return {};
    if (typeof data === 'object') return data;
    try {
        const parsed = JSON.parse(data);
        if (typeof parsed === 'string') return JSON.parse(parsed);
        return parsed;
    } catch (e) {
        return {};
    }
};

// --- SADC & Global Flag Helper ---
const getCountryFlag = (country) => {
    if (!country || country === 'Not specified') return '';
    const c = String(country).toLowerCase();
    
    // SADC Region
    if (c.includes('south africa') || c.includes('rsa') || c.includes('za')) return '🇿🇦';
    if (c.includes('lesotho')) return '🇱🇸';
    if (c.includes('eswatini') || c.includes('swaziland')) return '🇸🇿';
    if (c.includes('botswana')) return '🇧🇼';
    if (c.includes('namibia')) return '🇳🇦';
    if (c.includes('zimbabwe')) return '🇿🇼';
    if (c.includes('mozambique')) return '🇲🇿';
    if (c.includes('zambia')) return '🇿🇲';
    if (c.includes('angola')) return '🇦🇴';
    if (c.includes('malawi')) return '🇲🇼';
    if (c.includes('tanzania')) return '🇹🇿';
    if (c.includes('drc') || c.includes('congo')) return '🇨🇩';
    if (c.includes('madagascar')) return '🇲🇬';
    if (c.includes('mauritius')) return '🇲🇺';
    if (c.includes('seychelles')) return '🇸🇨';
    
    // Fallback for international
    return '🌍'; 
};

// --- ENTERPRISE COMPONENTS ---

const ConfirmActionModal = ({ open, title, message, onConfirm, onClose, confirmText = "Confirm", confirmColor = "primary" }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpOutlineIcon color={confirmColor} /> {title}
        </DialogTitle>
        <DialogContent>
            <DialogContentText>{message}</DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} color="inherit">Cancel</Button>
            <Button onClick={() => { onConfirm(); onClose(); }} variant="contained" color={confirmColor}>
                {confirmText}
            </Button>
        </DialogActions>
    </Dialog>
);

const AssignReviewerModal = ({ open, onClose, currentReviewerId, onAssign }) => {
    const { token, user } = useAuth();
    const navigate = useNavigate();
    const [reviewers, setReviewers] = useState([]);
    const [selectedId, setSelectedId] = useState(currentReviewerId || '');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setLoading(true);
            api.get('/admin/users', { headers: { Authorization: `Bearer ${token}` } })
                .then(res => {
                    const eligible = res.data.filter(u => ['Admin', 'Reviewer', 'SuperUser'].includes(u.Role) && u.IsActive);
                    setReviewers(eligible);
                    setSelectedId(currentReviewerId || '');
                })
                .catch(err => console.error(err))
                .finally(() => setLoading(false));
        }
    }, [open, token, currentReviewerId, user?.id]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIndIcon color="primary" /> Assign Reviewer
            </DialogTitle>
            <DialogContent>
                {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box> : (
                    <>
                        <DialogContentText sx={{ mb: 2 }}>Assign a team member to evaluate this application.</DialogContentText>
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel>Reviewer</InputLabel>
                            <Select value={selectedId} label="Reviewer" onChange={(e) => setSelectedId(e.target.value)}>
                                <MenuItem value=""><em>Unassigned</em></MenuItem>
                                {reviewers.map(r => (
                                    <MenuItem key={r.Id} value={r.Id}>
                                        {r.FirstNames} {r.Surname} {r.Id === user?.id ? '(Me)' : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={() => onAssign(selectedId)} variant="contained" disabled={loading}>Assign</Button>
            </DialogActions>
        </Dialog>
    );
};

const ReinstateModal = ({ open, onClose, onConfirm }) => {
    const [reason, setReason] = useState('');
    const maxLength = 2500;
    const minLength = 10;
    const handleConfirm = () => { if (reason.length >= minLength) { onConfirm(reason); setReason(''); } };
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><RestoreIcon color="primary" /> Reinstate Application</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>This action will make the application active again (Status: Submitted). <strong> A reason is mandatory for the audit log.</strong></DialogContentText>
                <TextField autoFocus margin="dense" label="Reason for reinstatement" type="text" fullWidth multiline rows={4} value={reason} onChange={(e) => setReason(e.target.value)} inputProps={{ maxLength: maxLength }} helperText={`${reason.length}/${maxLength} (Min: ${minLength})`} />
            </DialogContent>
            <DialogActions><Button onClick={onClose} color="inherit">Cancel</Button><Button onClick={handleConfirm} variant="contained" color="primary" disabled={reason.length < minLength}>Confirm Reinstate</Button></DialogActions>
        </Dialog>
    );
};

const AdminContractModal = ({ open, onClose, contractData, applicantName }) => {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><GavelIcon color="primary" /> Signed Contract Agreement</DialogTitle>
            <DialogContent dividers>
                {contractData ? (
                    <Box>
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="body2"><strong>Signed By:</strong> {applicantName || 'Unknown'}</Typography>
                            <Typography variant="body2"><strong>Date:</strong> {new Date(contractData.SignedAt).toLocaleString('en-ZA')}</Typography>
                            <Typography variant="body2"><strong>Type:</strong> {contractData.SignatureType}</Typography>
                            <Typography variant="body2"><strong>IP:</strong> {contractData.IpAddress || 'N/A'}</Typography>
                        </Box>
                        <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto', bgcolor: 'background.paper' }}>
                            <Typography component="div" sx={{ fontSize: '0.9rem', '& p': { mt: 0, mb: 1.5 }, '& strong': { fontWeight: 'bold' } }} dangerouslySetInnerHTML={{ __html: contractData.ContractContent || '' }} />
                        </Paper>
                        {contractData.SignatureType === 'Canvas' && contractData.SignatureData && (
                            <Box sx={{ mt: 3, textAlign: 'center' }}><Typography variant="caption" display="block">Digital Signature:</Typography><img src={contractData.SignatureData} alt="Signature" style={{ border: '1px solid #ccc', marginTop: '5px', maxHeight: '100px', backgroundColor: '#fff' }} /></Box>
                        )}
                    </Box>
                ) : <Alert severity="info">No signed contract found.</Alert>}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
};

const ScoringPanel = ({ token, fundId, applicationId, showToast }) => {
  const [criteria, setCriteria] = useState([]);
  const [scores, setScores] = useState({});
  const [totalScore, setTotalScore] = useState(0);
  const [maxScore, setMaxScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchScores = useCallback(async () => {
    if (!fundId || !applicationId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [rubricRes, scoresRes] = await Promise.all([
        api.get(`/admin/bursaries/${fundId}/rubric`, { headers: { Authorization: `Bearer ${token}` } }),
        api.get(`/admin/applications/${applicationId}/scores`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCriteria(rubricRes.data);
      const loadedScores = {};
      let total = 0;
      let max = 0;
      const savedScores = Array.isArray(scoresRes.data) ? scoresRes.data : [];
      rubricRes.data.forEach(c => {
        const loadedScore = savedScores.find(s => s.CriteriaId === c.Id);
        const score = loadedScore ? loadedScore.Score : 0;
        loadedScores[c.Id] = score;
        total += score;
        max += c.MaxScore;
      });
      setScores(loadedScores);
      setTotalScore(total);
      setMaxScore(max);
    } catch (error) { showToast("Failed to load scoring rubric.", "error"); } 
    finally { setLoading(false); }
  }, [fundId, applicationId, token, showToast]);

  useEffect(() => { fetchScores(); }, [fetchScores]);

  const handleScoreChange = (criteriaId, newValue) => {
    const newScores = { ...scores, [criteriaId]: newValue };
    setScores(newScores);
    setTotalScore(Object.values(newScores).reduce((acc, val) => acc + val, 0));
  };

  const handleSubmitScores = async () => {
    setSubmitting(true);
    const scoresArray = Object.entries(scores).map(([criteriaId, score]) => ({ criteriaId: parseInt(criteriaId), score }));
    try { 
        await api.post(`/admin/applications/${applicationId}/scores`, { scores: scoresArray }, { headers: { Authorization: `Bearer ${token}` } }); 
        showToast("Scores saved successfully.", "success");
    } 
    catch (error) { showToast("Failed to submit scores.", "error"); } 
    finally { setSubmitting(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>;
  if (criteria.length === 0) return <Card sx={{ mb: 3, p: 2 }}><Typography variant="body2">No rubric defined.</Typography></Card>;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6">Scoring Rubric</Typography>
        <Divider sx={{ my: 1 }} />
        {criteria.map(c => (
          <Box key={c.Id} sx={{ mb: 1 }}>
            <Typography gutterBottom>{c.CriteriaName} ({scores[c.Id] || 0} / {c.MaxScore})</Typography>
            <Slider value={scores[c.Id] || 0} onChange={(e, val) => handleScoreChange(c.Id, val)} valueLabelDisplay="auto" min={0} max={c.MaxScore} />
          </Box>
        ))}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, alignItems: 'center' }}>
          <Typography variant="h6">Total: {totalScore} / {maxScore}</Typography>
          <Button variant="contained" onClick={handleSubmitScores} disabled={submitting}>
              {submitting ? <CircularProgress size={20} /> : 'Save Scores'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

const InternalNotesPanel = ({ token, applicationId, showToast }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');

  const fetchNotes = useCallback(async () => {
    try {
      const res = await api.get(`/admin/applications/${applicationId}/notes`, { headers: { Authorization: `Bearer ${token}` }});
      setNotes(res.data || []);
    } catch (err) {} 
  }, [applicationId, token]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handlePostNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/admin/applications/${applicationId}/notes`, { Note: newNote }, { headers: { Authorization: `Bearer ${token}` }});
      setNewNote(''); fetchNotes();
    } catch (err) { showToast('Failed to post note.', 'error'); }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Internal Notes" avatar={<ForumIcon color="primary" />} />
      <CardContent>
        <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
          {notes.map(note => (
            <ListItem key={note.Id} alignItems="flex-start" disablePadding sx={{ mb: 1 }}>
              <ListItemText 
                primary={<Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{note.AdminName || 'Unknown'}</Typography><Typography variant="caption" color="text.secondary">{note.CreatedAt ? new Date(note.CreatedAt).toLocaleString() : ''}</Typography></Box>} 
                secondary={note.Note} 
                secondaryTypographyProps={{variant: 'body2', color: 'text.primary'}} 
              />
            </ListItem>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField label="Add note..." fullWidth size="small" value={newNote} onChange={(e) => setNewNote(e.target.value)} />
          <IconButton color="primary" onClick={handlePostNote}><SendIcon /></IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

const DisbursementPanel = ({ token, applicationId, showToast }) => {
  const [disbursements, setDisbursements] = useState([]);
  const [newPayment, setNewPayment] = useState({ Amount: '', PaymentType: 'Tuition', PaymentDate: '', ReferenceNumber: '' });

  const fetchDisbursements = useCallback(async () => {
    try {
      const res = await api.get(`/admin/applications/${applicationId}/disbursements`, { headers: { Authorization: `Bearer ${token}` }});
      setDisbursements(res.data || []);
    } catch (err) {} 
  }, [applicationId, token]);

  useEffect(() => { fetchDisbursements(); }, [fetchDisbursements]);

  const handleLogPayment = async () => {
    if (!newPayment.Amount || !newPayment.PaymentDate) return showToast("Amount and Date required.", "warning");
    try {
      await api.post(`/admin/applications/${applicationId}/disbursements`, newPayment, { headers: { Authorization: `Bearer ${token}` }});
      setNewPayment({ Amount: '', PaymentType: 'Tuition', PaymentDate: '', ReferenceNumber: '' });
      fetchDisbursements();
      showToast("Payment logged successfully.", "success");
    } catch (err) { showToast('Failed to log payment.', 'error'); }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Financial Disbursements" avatar={<PaymentsIcon color="primary" />} />
      <CardContent>
        <Typography variant="h6" color="success.main">Total: R {disbursements.reduce((a, b) => a + (parseFloat(b.Amount)||0), 0).toLocaleString()}</Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6} md={3}><TextField label="Amount" type="number" fullWidth size="small" value={newPayment.Amount} onChange={e=>setNewPayment({...newPayment, Amount:e.target.value})} /></Grid>
            <Grid item xs={6} md={3}><TextField label="Date" type="date" fullWidth size="small" InputLabelProps={{shrink:true}} value={newPayment.PaymentDate} onChange={e=>setNewPayment({...newPayment, PaymentDate:e.target.value})} /></Grid>
            <Grid item xs={6} md={3}>
                 <Select fullWidth size="small" value={newPayment.PaymentType} onChange={e=>setNewPayment({...newPayment, PaymentType:e.target.value})}>
                     <MenuItem value="Tuition">Tuition</MenuItem><MenuItem value="Stipend">Stipend</MenuItem><MenuItem value="Books">Books</MenuItem>
                 </Select>
            </Grid>
            <Grid item xs={6} md={3}><TextField label="Reference" fullWidth size="small" value={newPayment.ReferenceNumber} onChange={e=>setNewPayment({...newPayment, ReferenceNumber:e.target.value})} /></Grid>
            <Grid item xs={12}><Button variant="contained" fullWidth onClick={handleLogPayment}>Log Payment</Button></Grid>
        </Grid>
        <List dense sx={{ mt: 2 }}>
            {disbursements.map(d => (
                <ListItem key={d.Id} divider>
                    <ListItemText primary={`R ${d.Amount} - ${d.PaymentType}`} secondary={`${new Date(d.PaymentDate).toLocaleDateString('en-ZA')} by ${d.AdminName || 'Unknown'} ${d.ReferenceNumber ? `(Ref: ${d.ReferenceNumber})` : ''}`} />
                </ListItem>
            ))}
        </List>
      </CardContent>
    </Card>
  );
};

const ProgressPanel = ({ token, applicationId, onPreview, showToast }) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reviewingId, setReviewingId] = useState(null);
    const [editStatus, setEditStatus] = useState('');
    const [editNotes, setEditNotes] = useState('');

    const fetchReports = useCallback(() => {
        setLoading(true);
        api.get(`/admin/applications/${applicationId}/progress`, { headers: { Authorization: `Bearer ${token}` }})
           .then(res => setReports(res.data)).catch(e => console.error(e))
           .finally(() => setLoading(false));
    }, [applicationId, token]);

    useEffect(() => { fetchReports(); }, [fetchReports]);

    const handleOpenReview = (report) => {
        setReviewingId(report.Id);
        setEditStatus(report.Status || 'Pending');
        setEditNotes(report.AdminNotes || '');
    };

    const handleSaveReview = async (progressId) => {
        try {
            await api.put(`/admin/progress/${progressId}`, { status: editStatus, adminNotes: editNotes }, { headers: { Authorization: `Bearer ${token}` }});
            showToast("Progress report reviewed successfully.", "success");
            setReviewingId(null);
            fetchReports();
        } catch (e) { showToast("Failed to save progress review.", "error"); }
    };

    return (
        <Card sx={{ mb: 3 }}>
            <CardHeader title="Academic Progress" avatar={<SchoolIcon color="primary"/>} />
            <CardContent>
                {loading ? <CircularProgress size={24} /> : (
                    <List dense disablePadding>
                        {reports.map(r => (
                            <React.Fragment key={r.Id}>
                                <ListItem alignItems="flex-start" sx={{ flexDirection: 'column', alignItems: 'stretch', py: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: reviewingId === r.Id ? 2 : 0 }}>
                                        <Box>
                                            <Typography variant="subtitle2">{r.TermName}</Typography>
                                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                                                <Chip label={r.Status} size="small" color={r.Status === 'Verified' ? 'success' : r.Status === 'Rejected' ? 'error' : 'default'} />
                                                <Typography variant="caption" color="text.secondary">Submitted: {new Date(r.SubmittedAt).toLocaleDateString()}</Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button variant="outlined" size="small" onClick={() => onPreview(r.UploadPath)} startIcon={<VisibilityIcon />}>Preview</Button>
                                            <Button variant="contained" size="small" color="primary" onClick={() => handleOpenReview(r)}>Review</Button>
                                        </Box>
                                    </Box>

                                    {reviewingId === r.Id && (
                                        <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'background.default' }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={4}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Status</InputLabel>
                                                        <Select value={editStatus} label="Status" onChange={(e) => setEditStatus(e.target.value)}>
                                                            <MenuItem value="Pending">Pending</MenuItem><MenuItem value="Verified">Verified</MenuItem><MenuItem value="Rejected">Rejected</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} sm={8}><TextField fullWidth size="small" label="Admin Feedback (Visible to Student)" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} /></Grid>
                                                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                    <Button size="small" onClick={() => setReviewingId(null)}>Cancel</Button>
                                                    <Button size="small" variant="contained" onClick={() => handleSaveReview(r.Id)}>Save Review</Button>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    )}
                                </ListItem>
                                <Divider component="li" />
                            </React.Fragment>
                        ))}
                        {reports.length === 0 && <Typography variant="body2" sx={{ mt: 1 }}>No reports uploaded yet.</Typography>}
                    </List>
                )}
            </CardContent>
        </Card>
    );
};

const BankingDetailsCard = ({ applicant, token, onUpdate, showToast }) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const hasBankDetails = !!(applicant.BankName && applicant.AccountNumber);

    const handleVerifyBank = async () => {
        try {
            await api.put(`/admin/users/${applicant.UserId}/verify-bank`, { isVerified: !applicant.BankVerified }, { headers: { Authorization: `Bearer ${token}` } });
            showToast(`Bank account status updated.`, 'success');
            onUpdate(); 
        } catch (e) { showToast("Failed to update status.", "error"); }
    };

    return (
        <>
            <Card sx={{ mb: 3, border: '1px solid', borderColor: applicant.BankVerified ? 'success.main' : 'warning.main' }}>
                <CardHeader 
                    title="Banking Details" 
                    avatar={<AccountBalanceIcon color={applicant.BankVerified ? "success" : "action"} />} 
                    action={
                        <Button 
                            size="small" 
                            variant="outlined" 
                            color={applicant.BankVerified ? "error" : "success"}
                            startIcon={applicant.BankVerified ? <RuleIcon /> : <VerifiedUserIcon />}
                            onClick={() => setConfirmOpen(true)}
                            disabled={!hasBankDetails}
                        >
                            {applicant.BankVerified ? "Unverify" : "Verify"}
                        </Button>
                    }
                />
                <CardContent>
                    {hasBankDetails ? (
                        <Grid container spacing={2}>
                            <Grid item xs={6}><DetailItem title="Bank" data={applicant.BankName} /></Grid>
                            <Grid item xs={6}><DetailItem title="Account Type" data={applicant.AccountType} /></Grid>
                            <Grid item xs={6}><DetailItem title="Account Number" data={applicant.AccountNumber} /></Grid>
                            <Grid item xs={6}><DetailItem title="Branch Code" data={applicant.BranchCode} /></Grid>
                            <Grid item xs={12}><DetailItem title="Account Holder" data={applicant.AccountHolderName} /></Grid>
                        </Grid>
                    ) : (
                        <Alert severity="info">Applicant has not provided banking details yet.</Alert>
                    )}
                    {hasBankDetails && !applicant.BankVerified && (
                        <Alert severity="warning" size="small" sx={{ mt: 1 }}>
                            Details not yet verified. Check against proof of account.
                        </Alert>
                    )}
                </CardContent>
            </Card>

            <ConfirmActionModal 
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleVerifyBank}
                title={applicant.BankVerified ? "Remove Verification?" : "Verify Bank Account?"}
                message={`Are you sure you want to mark ${applicant.FirstNames}'s bank account as ${applicant.BankVerified ? 'UNVERIFIED' : 'VERIFIED'}?`}
                confirmText={applicant.BankVerified ? "Unverify" : "Verify Account"}
                confirmColor={applicant.BankVerified ? "error" : "success"}
            />
        </>
    );
};

// --- MAIN COMPONENT ---
const ReviewApplicationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [application, setApplication] = useState(null);
  
  // 🚨 FIXED: Global Profile & Academic State
  const [profileAcademicData, setProfileAcademicData] = useState([]);
  const [userProfile, setUserProfile] = useState({}); // Pulls Gender, Race, Profile Address

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [status, setStatus] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isReinstateOpen, setIsReinstateOpen] = useState(false);
  const [contractData, setContractData] = useState(null);
  const [isContractOpen, setIsContractOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isResetVibeOpen, setIsResetVibeOpen] = useState(false);
  const [resetVibeReason, setResetVibeReason] = useState('');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showToast = useCallback((msg, severity = 'success') => {
      setSnackbar({ open: true, message: msg, severity });
  }, []);

  // 🚨 FIXED: Bulletproof Academic Fetcher (Handles camelCase and wrappers)
  const fetchAcademicResults = useCallback(async (userId) => {
    if (!userId) return;
    try {
        const response = await api.get(`/admin/users/${userId}/academic`, { headers: { Authorization: `Bearer ${token}` } });
        const data = response.data?.data || response.data || [];
        setProfileAcademicData(Array.isArray(data) ? data : []);
    } catch (err) {
        console.error('Failed to load user academic results from profile.', err);
    }
  }, [token]);

  // 🚨 NEW: Global Profile Fetcher (For Gender, Race, etc.)
  // Update this specific function inside ReviewApplicationPage.js
const fetchUserProfile = useCallback(async (userId) => {
  if (!userId) return;
  try {
      const response = await api.get(`/admin/users/${userId}/profile`, { headers: { Authorization: `Bearer ${token}` } });
      
      // Debug line to see what the server is responding with
      //console.log("BrightSparc Debug - Raw Profile Payload:", response.data);

      // Clean extract: check if wrapped in profile property, otherwise take the data root
      const cleanProfile = response.data?.profile || response.data || {};
      setUserProfile(cleanProfile);
  } catch (err) {
      console.error('Failed to load global user profile.', err);
  }
}, [token]);

  // 🚨 FIXED: Triggers both fetches using safe ID checking
  const fetchApplication = useCallback(async () => {
    if (!token || !id) return;
    try {
        const response = await api.get(`/admin/applications/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        setApplication(response.data);
        setStatus(response.data.Status);
        setFeedback(response.data.Feedback || '');

        // Safe extraction of UserId regardless of backend casing
        const targetUserId = response.data.UserId || response.data.userId;
        if (targetUserId) {
            fetchAcademicResults(targetUserId);
            fetchUserProfile(targetUserId);
        }
    } catch (err) { setError('Failed to load application.'); } 
    finally { setLoading(false); }
  }, [id, token, fetchAcademicResults, fetchUserProfile]);

  useEffect(() => { fetchApplication(); }, [fetchApplication]);

  const handleSubmitReview = async () => {
    setIsSubmitting(true);
    try {
      await api.put(`/admin/applications/${id}`, { status, feedback }, { headers: { Authorization: `Bearer ${token}` } });
      showToast('Status updated successfully.', 'success');
      fetchApplication();
    } catch (err) { showToast('Failed to update status.', 'error'); } 
    finally { setIsSubmitting(false); }
  };

  const handleConfirmReinstate = async (reason) => {
        setIsReinstateOpen(false);
        try {
            await api.put(`/admin/applications/${application.Id}/reinstate`, { reinstateReason: reason }, { headers: { Authorization: `Bearer ${token}` } });
            showToast('Application reinstated successfully.', 'success');
            fetchApplication(); 
        } catch (e) {
            showToast(e.response?.data?.message || 'Failed to reinstate.', 'error');
        }
  };

  const handleAssignReviewer = async (reviewerId) => {
        try {
            await api.put(`/admin/applications/${application.Id}/assign`, { reviewerId }, { headers: { Authorization: `Bearer ${token}` } });
            showToast('Reviewer assigned successfully.', 'success');
            setIsAssignOpen(false);
            fetchApplication();
        } catch (e) {
            showToast('Failed to assign reviewer.', 'error');
        }
  };

  const handleResetVibeCheck = async () => {
    try {
        await api.delete(`/admin/users/${application.UserId}/vibe-check`, { 
            headers: { Authorization: `Bearer ${token}` },
            data: { reason: resetVibeReason } 
        });
        showToast('Vibe Check reset successfully.', 'success');
        setIsResetVibeOpen(false);
        setResetVibeReason('');
        fetchApplication(); 
    } catch (e) {
        showToast(e.response?.data?.message || 'Failed to reset Vibe Check.', 'error');
    }
  };

  const handleGenerateTaxCert = async () => {
      try {
          const res = await api.get(`/admin/tax/certificate/${application.Id}`, { headers: { Authorization: `Bearer ${token}` } });
          const data = res.data;
          
          const doc = new jsPDF();
          doc.text("BURSARY AWARD / TAX CERTIFICATE", 105, 20, { align: "center" });
          doc.text(`Certificate #: ${data.certificateId}`, 105, 26, { align: "center" });
          
          doc.setFontSize(10);
          doc.text("ISSUED BY:", 14, 40);
          doc.text(data.funder.name || '', 14, 46);
          doc.text(`Reg #: ${data.funder.regNumber || 'N/A'}`, 14, 51);
          doc.text(`Tax Ref: ${data.funder.taxRef || 'N/A'}`, 14, 56);
          const addressLines = doc.splitTextToSize(data.funder.address || 'N/A', 80);
          doc.text(addressLines, 14, 61);

          doc.text("ISSUED TO:", 120, 40);
          doc.text(data.recipient.name || '', 120, 46);
          doc.text(`ID: ${data.recipient.idNumber || ''}`, 120, 51);
          doc.text(`Bursary: ${data.bursary.name || ''}`, 120, 56);

          const tableBody = data.financials.transactions.map(t => [
              new Date(t.PaymentDate).toLocaleDateString(),
              t.PaymentType,
              t.ReferenceNumber || '-',
              `R ${parseFloat(t.Amount).toFixed(2)}`
          ]);

          autoTable(doc, { startY: 85, head: [['Date', 'Type', 'Reference', 'Amount']], body: tableBody, foot: [['', '', 'TOTAL:', `R ${data.financials.summary.Total.toFixed(2)}`]] });

          const pageCount = doc.internal.getNumberOfPages();
          for (let i = 1; i <= pageCount; i++) {
              doc.setPage(i);
              doc.setFontSize(9);
              doc.setFont("helvetica", "italic");
              doc.setTextColor(150, 150, 150); 
              doc.text('Powered by BrightSparc', doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
          }

          doc.save(`TaxCert_${data.recipient.idNumber}.pdf`);
          showToast('Tax Certificate generated.', 'success');
      } catch (e) {
          showToast('Failed to generate tax certificate.', 'error');
      }
  };

  const handleViewContract = async () => {
      try {
          const res = await api.get(`/contracts/${application.Id}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.data.status === 'Signed') { setContractData(res.data.contract); setIsContractOpen(true); } else { showToast("Contract not signed.", 'warning'); }
      } catch (e) { showToast("Failed to load contract.", 'error'); }
  };
  
  const handlePreviewDocument = (uploadPath) => {
    if (!uploadPath) return showToast("File path missing.", "error");
    setPreviewUrl(null); 
    setTimeout(() => {
        if (uploadPath.startsWith('http')) {
            setPreviewUrl(uploadPath);
        } else {
            const cleanPath = uploadPath.replace(/\\/g, '/').replace(/^\/?vault\//, '').replace(/^\/?uploads\//, '');
            setPreviewUrl(`${API_BASE_URL}/vault/${cleanPath}`);
        }
    }, 50);
  };
  
  const closePreview = () => setPreviewUrl(null);
  
  const handleVerifyDocument = async (docId, currentStatus) => {
    try {
        const newStatus = !currentStatus;
        await api.put(`/applicant/documents/${docId}/verify`, { isVerified: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
        fetchApplication(); 
        showToast(`Document marked as ${newStatus ? 'Verified' : 'Unverified'}.`, 'success');
    } catch (e) { showToast('Failed to update document status.', 'error'); }
  };

  // 🚨 FIXED: Download Full Application PDF now uses safe capitalization for profile data
  // 🚨 ENHANCED PDF EXPORT (Includes Admin Feedback, Rubric Scores & Internal Notes)
  const handleDownloadApplicationPDF = async () => {
        const doc = new jsPDF();
        let yPos = 20;

        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text(`Application Review: ${application.ApplicationRef || application.Id}`, 14, yPos);
        yPos += 8;

        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.text(`Bursary: ${application.FundName || 'Unknown'}`, 14, yPos);
        yPos += 6;
        doc.text(`Status: ${application.Status}`, 14, yPos);
        yPos += 6;
        doc.text(`Applicant: ${application.FullName} (${application.Email})`, 14, yPos);
        yPos += 10;

        const personalDetails = parseData(application.PersonalDetails);
        const academicInfo = parseData(application.AcademicInfo);
        const financialInfo = parseData(application.FinancialInfo);
        const financialData = financialInfo.financial || financialInfo;
        const address = personalDetails.address || {};
        const jbmarksData = parseData(application.JBMarksData);
        const guardianData = financialInfo.guardian || {};
        const popiaData = parseData(application.PopiaConsent);

        const addSection = (title, bodyArray, headerColor = [139, 125, 83]) => {
            autoTable(doc, {
                startY: yPos,
                head: [[title, 'Details']],
                body: bodyArray,
                theme: 'grid',
                headStyles: { fillColor: headerColor },
                styles: { fontSize: 10, cellPadding: 3 },
                columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 } }
            });
            yPos = doc.lastAutoTable.finalY + 10;
        };

        // Extract Citizenship safely for PDF
        const citizenshipVal = 
            personalDetails.Citizenship && String(personalDetails.Citizenship) !== 'null' ? String(personalDetails.Citizenship) :
            personalDetails.citizenship && String(personalDetails.citizenship) !== 'null' ? String(personalDetails.citizenship) :
            userProfile.Citizenship && String(userProfile.Citizenship) !== 'null' ? String(userProfile.Citizenship) :
            userProfile.citizenship && String(userProfile.citizenship) !== 'null' ? String(userProfile.citizenship) : 
            'Not specified';

        const personalBody = [
            ['ID Number', personalDetails.IdNumber || personalDetails.idNumber || userProfile.IdNumber || userProfile.idNumber || 'N/A'],
            ['Phone', personalDetails.CellNumber || personalDetails.cellNo || userProfile.CellNumber || userProfile.cellNumber || 'N/A'],
            ['Gender', userProfile.Gender || userProfile.gender || 'N/A'],
            ['Race', userProfile.Race || userProfile.race || 'N/A'],
            ['Citizenship', citizenshipVal],
            ['T-Shirt Size', personalDetails.tShirtSize || 'N/A'],
            ['Physical Address', [address.physicalUnit, address.physicalStreet, address.physicalTown, address.physicalCity, address.physicalCode].filter(Boolean).join(', ') || 'N/A']
        ];
        addSection('Personal Details', personalBody);

        // 🚨 1. ADMIN DECISION & REJECTION / FEEDBACK SECTION
        const decisionBody = [
            ['Final Application Status', application.Status || 'Under Review'],
            ['Assigned Reviewer', application.ReviewerName || 'Unassigned'],
            ['Funder Feedback / Reason', application.Feedback || (application.Status === 'Rejected' ? 'No explicit reason specified' : 'None provided')]
        ];
        addSection('Reviewer Decision & Feedback', decisionBody, [192, 57, 43]); // Highlighted with warning header

        // 🚨 2. FETCH & INCLUDE RUBRIC SCORING BREAKDOWN
        try {
            const [rubricRes, scoresRes] = await Promise.all([
                api.get(`/admin/bursaries/${application.FundId}/rubric`, { headers: { Authorization: `Bearer ${token}` } }),
                api.get(`/admin/applications/${application.Id}/scores`, { headers: { Authorization: `Bearer ${token}` } })
            ]);

            const rubricList = Array.isArray(rubricRes.data) ? rubricRes.data : [];
            const savedScores = Array.isArray(scoresRes.data) ? scoresRes.data : [];

            if (rubricList.length > 0) {
                let totalScored = 0;
                let totalMax = 0;

                const rubricRows = rubricList.map(c => {
                    const match = savedScores.find(s => s.CriteriaId === c.Id);
                    const score = match ? match.Score : 0;
                    totalScored += score;
                    totalMax += c.MaxScore;
                    return [c.CriteriaName, `${score} / ${c.MaxScore}`];
                });

                rubricRows.push(['TOTAL EVALUATION SCORE', `${totalScored} / ${totalMax}`]);

                autoTable(doc, {
                    startY: yPos,
                    head: [['Rubric Criteria', 'Score']],
                    body: rubricRows,
                    theme: 'striped',
                    headStyles: { fillColor: [44, 54, 94] },
                    styles: { fontSize: 9, cellPadding: 2.5 }
                });
                yPos = doc.lastAutoTable.finalY + 10;
            }
        } catch (rubricErr) {
            console.warn("Could not append rubric scores to PDF:", rubricErr);
        }

        const academicBody = [
            ['Current Status', academicInfo.currentStatus || 'N/A'],
            ['Institution', academicInfo.currentInstitutionName || academicInfo.intendedInstitution || 'N/A'],
            ['Course/Field', academicInfo.currentCourse || academicInfo.intendedCourse || 'N/A'],
            ['Level of Study', academicInfo.currentLevel || academicInfo.intendedLevel || 'N/A'],
        ];
        
        if (academicInfo.subjects) {
            const subjects = Object.entries(academicInfo.subjects)
                .filter(([_, data]) => data.selected)
                .map(([key, data]) => `${key.replace(/([A-Z])/g, ' $1').toUpperCase()}: ${data.level || 'Selected'}`)
                .join('\n');
            if (subjects) academicBody.push(['High School Subjects', subjects]);
        }

        if (profileAcademicData.length > 0) {
             const detailedResults = profileAcademicData.map(res => {
                 const subject = res.Subject || res.subject || 'Unknown Subject';
                 const year = res.YearAchieved || res.yearAchieved || res.year || 'N/A';
                 const mark = res.Mark || res.mark || '0';
                 return `${subject} (${year}): ${mark}%`;
             }).join('\n');
             academicBody.push(['Detailed Profile Results', detailedResults]);
        }

        addSection('Academic Information', academicBody);

        const finBody = [
            ['Household Income', isNaN(financialData.householdIncome) ? financialData.householdIncome : `R ${parseFloat(financialData.householdIncome || 0).toLocaleString('en-ZA')}`],
            ['Dependants', financialData.numberOfDependants || 'N/A'],
            ['Requires Accommodation', financialData.needsAccommodation || 'N/A'],
            ['Other Aid', financialData.otherAid || 'N/A']
        ];
        addSection('Financial & Logistics', finBody);

        if (Object.keys(jbmarksData).length > 0 || Object.keys(guardianData).length > 0) {
            if (yPos > 250) { doc.addPage(); yPos = 20; }
            const jbBody = [
                ['NUM Member Status', jbmarksData.numStatus || 'N/A'],
                ['Member First Names', guardianData.firstNames || 'N/A'],
                ['Member Surname', guardianData.surname || 'N/A'],
                ['Member ID Number', guardianData.idNumber || 'N/A'],
                ['NUM Region', jbmarksData.region || 'N/A'],
                ['NUM Branch', jbmarksData.branch || 'N/A'],
                ['Company Name', jbmarksData.companyName || 'N/A'],
                ['Industry Number', jbmarksData.industryNo || 'N/A'],
                ['Industry Sector', guardianData.industry || 'N/A']
            ];
            addSection('NUM & Member Details', jbBody);
        }

        if (application.Motivation) {
            if (yPos > 250) { doc.addPage(); yPos = 20; }
            doc.setFontSize(11);
            doc.setFont("helvetica", "bold");
            doc.text('Applicant Motivation:', 14, yPos);
            yPos += 6;
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            const splitMotivation = doc.splitTextToSize(application.Motivation, 180);
            doc.text(splitMotivation, 14, yPos);
            yPos += (splitMotivation.length * 5) + 10;
        }

        // 🚨 3. INCLUDE INTERNAL STAFF NOTES IN THE PDF EXPORT
        try {
            const notesRes = await api.get(`/admin/applications/${application.Id}/notes`, { headers: { Authorization: `Bearer ${token}` } });
            const notesList = Array.isArray(notesRes.data) ? notesRes.data : [];
            
            if (notesList.length > 0) {
                if (yPos > 240) { doc.addPage(); yPos = 20; }
                const notesBody = notesList.map(n => [
                    n.AdminName || 'Staff Member',
                    n.Note || '',
                    n.CreatedAt ? new Date(n.CreatedAt).toLocaleDateString() : ''
                ]);

                autoTable(doc, {
                    startY: yPos,
                    head: [['Staff Member', 'Internal Note', 'Date']],
                    body: notesBody,
                    theme: 'striped',
                    headStyles: { fillColor: [52, 73, 94] },
                    styles: { fontSize: 8.5 }
                });
                yPos = doc.lastAutoTable.finalY + 10;
            }
        } catch (notesErr) {
            console.warn("Could not fetch internal notes for PDF:", notesErr);
        }

        if (Array.isArray(application.documents) && application.documents.length > 0) {
            if (yPos > 250) { doc.addPage(); yPos = 20; }
            const docBody = application.documents.map(d => [
                d.FileType?.replace(/_/g, ' ') || 'Unknown',
                d.FileName || 'Unnamed file',
                d.IsVerified ? 'Verified' : 'Pending Verification'
            ]);
            
            autoTable(doc, { startY: yPos, head: [['Document Type', 'File Name', 'Verification Status']], body: docBody, theme: 'striped', styles: { fontSize: 9 } });
            yPos = doc.lastAutoTable.finalY + 10;
        }

        if (yPos > 250) { doc.addPage(); yPos = 20; }
        const consentBody = [
            ['JB Marks Conditions Ack.', jbmarksData.eligibilityAck ? 'Yes, acknowledged and accepted' : 'No'],
            ['POPIA Signed By', popiaData.fullName || 'N/A'],
            ['POPIA Sign Location', popiaData.location || 'N/A'],
            ['POPIA Consent Granted', popiaData.signatureCheckbox ? 'Yes' : 'No']
        ];
        addSection('Legal & Consent Declarations', consentBody);

        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(9);
            doc.setFont("helvetica", "italic");
            doc.setTextColor(150, 150, 150); 
            doc.text('Powered by BrightSparc', doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
        }

        doc.save(`Application_${application.ApplicationRef || application.Id}_Export.pdf`);
        showToast("Application downloaded successfully.", "success");
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Container sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!application) return <Container sx={{ mt: 4 }}><Alert severity="warning">App not found</Alert></Container>;

  const personalDetails = parseData(application.PersonalDetails);
  const academicInfo = parseData(application.AcademicInfo);
  const financialInfo = parseData(application.FinancialInfo);
  const financialData = financialInfo.financial || financialInfo;
  const address = personalDetails.address || {};
  const canReinstate = ['Withdrawn', 'Rejected'].includes(application.Status);
  const isFunded = ['Approved', 'Funded'].includes(application.Status);
  const isContractSigned = application.ContractStatus === 'Signed';

  const jbmarksData = parseData(application.JBMarksData);
  const guardianData = financialInfo.guardian || {};
  const isJBMarksTenant = Object.keys(jbmarksData).length > 0 || Object.keys(guardianData).length > 0;
  const popiaData = parseData(application.PopiaConsent);

  return (
    <Box sx={{ width: '100%', height: 'calc(100vh - 64px)', overflow: 'hidden', display: 'flex', bgcolor: 'background.default' }}>
        {/* Left Panel */}
        {previewUrl && (
            <Box sx={{ flex: 1, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', height: '100%' }}>
                <AppBar position="static" color="default" elevation={1} sx={{ zIndex: 10 }}>
                    <Toolbar variant="dense" sx={{ justifyContent: 'space-between' }}>
                        <Typography variant="subtitle2" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}><ArticleIcon sx={{ mr: 1 }} /> Document Viewer</Typography>
                        <Box>
                            <IconButton href={previewUrl} target="_blank" title="Open in New Tab" size="small" sx={{ mr: 1 }}><OpenInNewIcon /></IconButton>
                            <Button variant="contained" color="primary" size="small" onClick={closePreview} startIcon={<CloseIcon />}>Close Preview</Button>
                        </Box>
                    </Toolbar>
                </AppBar>
                <Box sx={{ flexGrow: 1, position: 'relative', overflow: 'hidden', bgcolor: '#000' }}>
                    <iframe key={previewUrl} src={previewUrl} style={{ border: 'none', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} title="Document Preview" />
                </Box>
            </Box>
        )}
        
        {/* Right Panel */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
             <Container maxWidth="lg">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                     
                     <Box>
                        <Typography variant="h4" fontWeight="bold" color="text.primary">
                            Ref: {application.ApplicationRef || application.Id}
                        </Typography>
                        
                        <Typography variant="h6" color="primary.main" fontWeight="bold" sx={{ mt: 0.5, mb: 1 }}>
                            {application.FundName ? `Bursary: ${application.FundName}` : 'Bursary: Unknown Fund'}
                        </Typography>

                        {application.ReviewerName && (
                            <Chip label={`Reviewer: ${application.ReviewerName}`} size="small" avatar={<Avatar>{application.ReviewerName[0]}</Avatar>} sx={{ mt: 1 }} />
                        )}

                    {application?.archetype && (
                        <Box sx={{ mt: 2, mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Chip 
                                        icon={<AutoAwesomeIcon sx={{ color: '#ffeb3b !important', fontSize: '24px !important' }} />} 
                                        label={`AI Profile: ${application.archetype}`} 
                                        sx={{ background: 'linear-gradient(135deg, #4a148c 0%, #7b1fa2 100%)', color: 'white', fontWeight: '900', fontSize: '1.1rem', py: 2.5, px: 2, boxShadow: 3 }} 
                                    />
                                    {application?.archetype_message && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontStyle: 'italic', maxWidth: 600 }}>
                                            "{application.archetype_message}"
                                        </Typography>
                                    )}
                                </Box>
                                
                                {['Admin', 'SuperUser'].includes(user?.role || user?.Role) && (
                                    <Button variant="outlined" color="error" size="small" onClick={() => setIsResetVibeOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                                        Reset Profile
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    )}
                     </Box>
                     
                     <Box>
                        <Button variant="outlined" color="secondary" startIcon={<PictureAsPdfIcon />} onClick={handleDownloadApplicationPDF} sx={{ mr: 1 }}>
                            Export PDF
                        </Button>
                        <Button variant="outlined" startIcon={<AssignmentIndIcon />} onClick={() => setIsAssignOpen(true)} sx={{ mr: 1 }}>
                            Assign
                        </Button>
                        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/search')}>Back</Button>
                     </Box>
                </Box>
                
                {canReinstate && (
                    <Alert severity="warning" action={
                        <Button color="inherit" size="small" onClick={() => setIsReinstateOpen(true)} startIcon={<RestoreIcon />}>
                            Reinstate
                        </Button>
                    } sx={{ mb: 3 }}>
                        This application is currently <strong>{application.Status}</strong>.
                    </Alert>
                )}
                
                <Grid container spacing={3}>
                    {!previewUrl && (
                        <Grid item xs={12} md={8}>

                            {/* --- 1. Applicant Details --- */}
                            <Card sx={{ mb: 3 }}>
                                <CardHeader title="Applicant Details" />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}><DetailItem title="Full Name" data={application.FullName} /></Grid>
                                        <Grid item xs={6}><DetailItem title="Email" data={application.Email} /></Grid>
                                        
                                        <Grid item xs={6}>
                                            <DetailItem 
                                                title="ID Number" 
                                                data={personalDetails.IdNumber || personalDetails.idNumber || userProfile.IDNumber || userProfile.IdNumber || userProfile.id_number || 'N/A'} 
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <DetailItem 
                                                title="Phone" 
                                                data={personalDetails.CellNumber || personalDetails.cellNo || userProfile.CellNumber || userProfile.cell_number || 'N/A'} 
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={6}>
                                            <DetailItem 
                                                title="Gender (From Profile)" 
                                                data={
                                                    personalDetails.Gender && String(personalDetails.Gender) !== 'null' ? String(personalDetails.Gender) :
                                                    personalDetails.gender && String(personalDetails.gender) !== 'null' ? String(personalDetails.gender) :
                                                    userProfile.Gender && String(userProfile.Gender) !== 'null' ? String(userProfile.Gender) :
                                                    userProfile.gender && String(userProfile.gender) !== 'null' ? String(userProfile.gender) : 
                                                    'Not specified'
                                                } 
                                            />
                                        </Grid>

                                        <Grid item xs={6}>
                                            <DetailItem 
                                                title="Ethnicity (From Profile)" 
                                                data={
                                                    personalDetails.Race && String(personalDetails.Race) !== 'null' ? String(personalDetails.Race) :
                                                    personalDetails.race && String(personalDetails.race) !== 'null' ? String(personalDetails.race) :
                                                    userProfile.Race && String(userProfile.Race) !== 'null' ? String(userProfile.Race) :
                                                    userProfile.race && String(userProfile.race) !== 'null' ? String(userProfile.race) : 
                                                    'Not specified'
                                                } 
                                            />
                                        </Grid>

                                        {/* 🚨 NEW: Citizenship with Flag Mapping */}
                                        <Grid item xs={6}>
                                            <DetailItem 
                                                title="Citizenship (From Profile)" 
                                                data={(() => {
                                                    const citizenshipData = 
                                                        personalDetails.Citizenship && String(personalDetails.Citizenship) !== 'null' ? String(personalDetails.Citizenship) :
                                                        personalDetails.citizenship && String(personalDetails.citizenship) !== 'null' ? String(personalDetails.citizenship) :
                                                        userProfile.Citizenship && String(userProfile.Citizenship) !== 'null' ? String(userProfile.Citizenship) :
                                                        userProfile.citizenship && String(userProfile.citizenship) !== 'null' ? String(userProfile.citizenship) : 
                                                        'Not specified';
                                                    
                                                    return citizenshipData !== 'Not specified' ? `${getCountryFlag(citizenshipData)} ${citizenshipData}` : 'Not specified';
                                                })()} 
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={6}><DetailItem title="T-Shirt Size" data={personalDetails.tShirtSize} /></Grid>
                                        <Grid item xs={12}>
                                            <DetailItem title="Address" data={[address.physicalUnit, address.physicalStreet, address.physicalTown, address.physicalCity, address.physicalCode].filter(Boolean).join(', ') || userProfile.Address || userProfile.address || 'N/A'} />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* --- NUM & Member Details --- */}
                            {isJBMarksTenant && (
                                <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'primary.main' }}>
                                    <CardHeader title="NUM Member Details" sx={{ bgcolor: 'action.hover' }} />
                                    <CardContent>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}><DetailItem title="Is NUM Member?" data={jbmarksData.numStatus} /></Grid>
                                            <Grid item xs={12} sm={6}><DetailItem title="NUM Region" data={jbmarksData.region} /></Grid>
                                            <Grid item xs={12} sm={6}><DetailItem title="NUM Branch" data={jbmarksData.branch} /></Grid>
                                            <Grid item xs={12} sm={6}><DetailItem title="Company Name" data={jbmarksData.companyName} /></Grid>
                                            <Grid item xs={12} sm={6}><DetailItem title="Industry Sector" data={guardianData.industry} /></Grid>
                                            <Grid item xs={12} sm={6}><DetailItem title="Industry Number" data={jbmarksData.industryNo} /></Grid>
                                            <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                                            <Grid item xs={12} sm={4}><DetailItem title="Member First Name" data={guardianData.firstNames} /></Grid>
                                            <Grid item xs={12} sm={4}><DetailItem title="Member Surname" data={guardianData.surname} /></Grid>
                                            <Grid item xs={12} sm={4}><DetailItem title="Member ID" data={guardianData.idNumber} /></Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            )}

                            {/* --- 2. Motivation --- */}
                            <Card sx={{ mb: 3 }}>
                                <CardHeader title="Motivation" />
                                <CardContent>
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{application.Motivation || 'Not provided'}</Typography>
                                    </Paper>
                                </CardContent>
                            </Card>

                            {/* --- 3. Academic & Financial --- */}
                            <Card sx={{ mb: 3 }}>
                                <CardHeader title="Academic & Financial" />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}><DetailItem title="Current Status" data={academicInfo.currentStatus} /></Grid>
                                        <Grid item xs={6}><DetailItem title="Intended Level" data={academicInfo.intendedLevel} /></Grid>
                                        
                                        <Grid item xs={12}><Divider sx={{ my: 1 }}>Choice 1 (Primary)</Divider></Grid>
                                        <Grid item xs={6}><DetailItem title="Institution" data={academicInfo.intendedInstitution1 || academicInfo.intendedInstitution || 'N/A'} /></Grid>
                                        <Grid item xs={6}><DetailItem title="Course/Field" data={academicInfo.intendedCourse1 || academicInfo.intendedCourse || 'N/A'} /></Grid>

                                        {academicInfo.intendedInstitution2 && (
                                            <>
                                                <Grid item xs={12}><Divider sx={{ my: 1 }}>Choice 2</Divider></Grid>
                                                <Grid item xs={6}><DetailItem title="Institution" data={academicInfo.intendedInstitution2} /></Grid>
                                                <Grid item xs={6}><DetailItem title="Course/Field" data={academicInfo.intendedCourse2} /></Grid>
                                            </>
                                        )}

                                        {academicInfo.intendedInstitution3 && (
                                            <>
                                                <Grid item xs={12}><Divider sx={{ my: 1 }}>Choice 3</Divider></Grid>
                                                <Grid item xs={6}><DetailItem title="Institution" data={academicInfo.intendedInstitution3} /></Grid>
                                                <Grid item xs={6}><DetailItem title="Course/Field" data={academicInfo.intendedCourse3} /></Grid>
                                            </>
                                        )}
                                        
                                        {/* High School Subjects */}
                                        {academicInfo.subjects && Object.keys(academicInfo.subjects).some(k => academicInfo.subjects[k].selected) && (
                                            <Grid item xs={12}>
                                                <Divider sx={{ my: 1 }} />
                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                                                    High School Subjects & Grades (From Application Form)
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {Object.entries(academicInfo.subjects).filter(([_, data]) => data.selected).map(([key, data]) => {
                                                        const subjectName = key.replace(/([A-Z])/g, ' $1').trim();
                                                        const cleanName = subjectName.charAt(0).toUpperCase() + subjectName.slice(1);
                                                        return (
                                                            <Chip 
                                                                key={key} 
                                                                label={`${cleanName}: ${data.level || 'Selected'}`} 
                                                                size="small" color="primary" variant="outlined" 
                                                                sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            </Grid>
                                        )}
                                        
                                        {/* 🚨 FIXED UI: Case-insensitive extraction for Academic Results rendering */}
                                        {profileAcademicData.length > 0 && (
                                            <Grid item xs={12}>
                                                <Divider sx={{ my: 2 }} />
                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                                                    Detailed Academic Results (Pulled from Applicant Profile)
                                                </Typography>
                                                <Paper variant="outlined">
                                                    <List dense disablePadding>
                                                        {profileAcademicData.map((res, idx) => (
                                                            <ListItem key={idx} divider={idx !== profileAcademicData.length - 1}>
                                                                <ListItemText 
                                                                    primary={res.Subject || res.subject} 
                                                                    secondary={`Year: ${res.YearAchieved || res.yearAchieved || res.year || 'N/A'}`} 
                                                                />
                                                                <Typography 
                                                                    variant="body2" 
                                                                    fontWeight="bold" 
                                                                    color={parseFloat(res.Mark || res.mark || 0) >= 50 ? 'success.main' : 'error.main'}
                                                                >
                                                                    {res.Mark || res.mark}%
                                                                </Typography>
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Paper>
                                            </Grid>
                                        )}

                                        <Grid item xs={12}><Divider sx={{ my: 2 }} /></Grid>
                                        
                                        <Grid item xs={6}><DetailItem title="Annual Household Income" data={financialData.householdIncome || 'N/A'} /></Grid>
                                        <Grid item xs={6}><DetailItem title="Dependants" data={financialData.numberOfDependants || 'N/A'} /></Grid>
                                        <Grid item xs={6}><DetailItem title="Other Aid" data={financialData.otherAid} /></Grid>
                                        <Grid item xs={6}><DetailItem title="Needs Accommodation" data={financialData.needsAccommodation} /></Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* --- 4. Documents (With Verification & Fresh Upload Badges) --- */}
                            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'primary.main' }}>
                                <CardHeader 
                                    title="Attached Documents" 
                                    titleTypographyProps={{ variant: 'h6' }} 
                                    avatar={<DescriptionIcon color="primary" />} 
                                />
                                <CardContent>
                                {Array.isArray(application.documents) && application.documents.length > 0 ? (
                                    <List dense disablePadding>
                                        {application.documents.map((doc) => (
                                            <ListItem 
                                                key={doc.Id} 
                                                divider 
                                                secondaryAction={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Tooltip title={doc.IsVerified ? "Mark as Unverified" : "Mark as Verified"}>
                                                            <IconButton 
                                                                onClick={() => handleVerifyDocument(doc.Id, doc.IsVerified)}
                                                                color={doc.IsVerified ? "success" : "default"}
                                                                size="small"
                                                            >
                                                                {doc.IsVerified ? <VerifiedUserIcon /> : <RuleIcon />}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Button 
                                                            size="small" 
                                                            variant={previewUrl?.includes(doc.UploadPath) ? "contained" : "outlined"} 
                                                            onClick={() => handlePreviewDocument(doc.UploadPath)} 
                                                            startIcon={<VisibilityIcon />}
                                                        >
                                                            Preview
                                                        </Button>
                                                    </Box>
                                                }
                                            >
                                                <ListItemIcon><ArticleIcon /></ListItemIcon>
                                                <ListItemText 
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <span>{doc.FileType?.replace(/_/g, ' ') || 'Unknown'}</span>
                                                            {!doc.IsVerified && (
                                                                <Chip 
                                                                    label="Fresh Upload" 
                                                                    size="small" 
                                                                    color="warning" 
                                                                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 'bold' }} 
                                                                />
                                                            )}
                                                        </Box>
                                                    } 
                                                    secondary={`${doc.FileName || 'Unnamed file'} ${doc.AttachedAt ? `• Attached: ${new Date(doc.AttachedAt).toLocaleDateString()}` : ''}`} 
                                                    sx={{ pr: 12 }} 
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">No documents uploaded.</Typography>
                                )}
                                </CardContent>
                            </Card>


                            {/* --- 5. Legal & Consent Declarations --- */}
                            <Card sx={{ mb: 3 }}>
                                <CardHeader title="Legal & Consent Declarations" />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}><DetailItem title="JB Marks Conditions Acknowledgment" data={jbmarksData.eligibilityAck ? "Yes, acknowledged and accepted" : "Not acknowledged / Legacy Application"} /></Grid>
                                        <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                                        <Grid item xs={12}><Typography variant="subtitle2" color="primary" gutterBottom>POPIA Consent (Digital Signature)</Typography></Grid>
                                        <Grid item xs={6}><DetailItem title="Signed By (Full Name)" data={popiaData.fullName} /></Grid>
                                        <Grid item xs={6}><DetailItem title="Sign Location" data={popiaData.location} /></Grid>
                                        <Grid item xs={12}><DetailItem title="Consent Granted" data={popiaData.signatureCheckbox ? "Yes, accepted POPIA terms on behalf of digital signature" : "Not accepted / Legacy Application"} /></Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* --- Contract & Banking (Only for Funded) --- */}
                            {isFunded && (
                                <>
                                     <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                                         <Button variant="outlined" startIcon={<ReceiptLongIcon />} onClick={handleGenerateTaxCert} color="secondary">
                                             Download Tax Certificate
                                         </Button>
                                         {isContractSigned ? (
                                              <Button variant="contained" color="success" startIcon={<GavelIcon />} onClick={handleViewContract}>View Signed Contract</Button>
                                         ) : (
                                              <Chip label="Contract Pending" color="warning" variant="outlined" icon={<GavelIcon />} />
                                         )}
                                     </Box>

                                    {['Admin', 'Finance', 'SuperUser'].includes(user?.role || user?.Role) && (
                                        <BankingDetailsCard applicant={application} token={token} onUpdate={fetchApplication} showToast={showToast} />
                                    )}
                                    
                                    <DisbursementPanel token={token} applicationId={application.Id} showToast={showToast} />
                                    <ProgressPanel token={token} applicationId={application.Id} onPreview={handlePreviewDocument} showToast={showToast} />
                                </>
                            )}
                        </Grid>
                    )}

                    <Grid item xs={12} md={previewUrl ? 12 : 4}>
                         <Box sx={{ position: previewUrl ? 'static' : 'sticky', top: '20px' }}>
                            <ScoringPanel token={token} fundId={application.FundId} applicationId={application.Id} showToast={showToast} />
                            
                            <Card elevation={4} sx={{ mb: 3 }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Final Decision</Typography>
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel>Status</InputLabel>
                                        <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
                                            <MenuItem value="Submitted">Submitted</MenuItem>
                                            <MenuItem value="Under Review">Under Review</MenuItem>
                                            <MenuItem value="Shortlisted" sx={{ color: 'info.main' }}>Shortlisted</MenuItem>
                                            <MenuItem value="Interview" sx={{ color: 'warning.main' }}>Interview Required</MenuItem>
                                            <Divider />
                                            <MenuItem value="Approved"><Typography color="success.main" fontWeight="bold">Approved</Typography></MenuItem>
                                            <MenuItem value="Funded"><Typography color="success.main" fontWeight="bold">Funded</Typography></MenuItem>
                                            <Divider />
                                            <MenuItem value="Rejected"><Typography color="error.main" fontWeight="bold">Rejected</Typography></MenuItem>
                                            <MenuItem value="Withdrawn" disabled><Typography color="text.disabled">Withdrawn</Typography></MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField label="Feedback for Applicant" multiline rows={4} fullWidth value={feedback} onChange={(e) => setFeedback(e.target.value)} sx={{ mb: 2 }} />
                                    <Button variant="contained" fullWidth onClick={handleSubmitReview} disabled={isSubmitting} size="large">
                                        {isSubmitting ? <CircularProgress size={24} /> : 'Submit Review'}
                                    </Button>
                                </CardContent>
                            </Card>
                            
                            <InternalNotesPanel token={token} applicationId={application.Id} showToast={showToast} />
                        </Box>
                    </Grid>
                </Grid>
                <Dialog open={isResetVibeOpen} onClose={() => setIsResetVibeOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AutoAwesomeIcon color="error" /> Reset AI Profile
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2 }}>
                            This will permanently delete the applicant's current AI Archetype and allow them to retake the Vibe Check game. <strong>A reason is mandatory.</strong>
                        </DialogContentText>
                        <TextField autoFocus margin="dense" label="Reason for Reset" fullWidth multiline rows={3} value={resetVibeReason} onChange={(e) => setResetVibeReason(e.target.value)} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsResetVibeOpen(false)} color="inherit">Cancel</Button>
                        <Button onClick={handleResetVibeCheck} variant="contained" color="error" disabled={resetVibeReason.length < 10}>Confirm Reset</Button>
                    </DialogActions>
                </Dialog>
             </Container>
        </Box>

        <AssignReviewerModal open={isAssignOpen} onClose={() => setIsAssignOpen(false)} currentReviewerId={application.ReviewerId} onAssign={handleAssignReviewer} />
        <ReinstateModal open={isReinstateOpen} onClose={() => setIsReinstateOpen(false)} onConfirm={handleConfirmReinstate} />
        <AdminContractModal open={isContractOpen} onClose={() => setIsContractOpen(false)} contractData={contractData} applicantName={application?.FullName} />
        
        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
        </Snackbar>
    </Box>
  );
}; 

export default ReviewApplicationPage;
